import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface MediaItem {
  id: string;
  title: string;
  description: string;
  storage_path: string;
  media_type: 'image' | 'video';
  is_featured: boolean;
  is_carousel: boolean;
  created_at: string;
  url?: string;
}

type GridTab = 'posts' | 'reels' | 'tagged';

/** Served from `/public/jacky-circuit-logo.png` */
const GALLERY_BRAND_LOGO = '/jacky-circuit-logo.png';

export default function Gallery() {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventCount, setEventCount] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<GridTab>('posts');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchMediaItems();
    fetchEventCount();
  }, []);

  const fetchEventCount = async () => {
    try {
      const { count, error } = await supabase.from('events').select('*', { count: 'exact', head: true });
      if (error) throw error;
      setEventCount(count ?? 0);
    } catch {
      setEventCount(null);
    }
  };

  const fetchMediaItems = async () => {
    try {
      const { data, error } = await supabase.from('gallery_media').select('*').order('created_at', { ascending: false });

      if (error) throw error;

      const itemsWithUrls = await Promise.all(
        (data || []).map(async (item) => {
          const {
            data: { publicUrl },
          } = supabase.storage.from('gallery').getPublicUrl(item.storage_path);

          return {
            ...item,
            url: publicUrl,
          };
        })
      );

      setMediaItems(itemsWithUrls);
    } catch (error) {
      console.error('Error fetching media items:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (activeTab === 'reels') return mediaItems.filter((m) => m.media_type === 'video');
    if (activeTab === 'tagged') return mediaItems.filter((m) => m.is_featured);
    return mediaItems;
  }, [mediaItems, activeTab]);

  const lightboxItem = lightboxIndex !== null ? filteredItems[lightboxIndex] : null;

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft' && lightboxIndex > 0) setLightboxIndex((i) => (i !== null ? i - 1 : i));
      if (e.key === 'ArrowRight' && lightboxIndex < filteredItems.length - 1) {
        setLightboxIndex((i) => (i !== null ? i + 1 : i));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, filteredItems.length]);

  useEffect(() => {
    if (lightboxIndex === null) {
      document.body.style.overflow = '';
    } else {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxIndex]);

  const preventContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  const preventKeyboardSave = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      return false;
    }
  };

  const openAt = (index: number) => setLightboxIndex(index);

  const closeLightbox = () => setLightboxIndex(null);

  const goPrev = () => {
    if (lightboxIndex === null || lightboxIndex <= 0) return;
    setLightboxIndex(lightboxIndex - 1);
  };

  const goNext = () => {
    if (lightboxIndex === null || lightboxIndex >= filteredItems.length - 1) return;
    setLightboxIndex(lightboxIndex + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="font-mono text-sm uppercase tracking-widest text-zinc-500">Loading gallery…</div>
      </div>
    );
  }

  const tabs: { id: GridTab; label: string }[] = [
    { id: 'posts', label: 'Posts' },
    { id: 'reels', label: 'Reels' },
    { id: 'tagged', label: 'Spotlights' },
  ];

  return (
    <div
      className="min-h-screen bg-black text-white border-t border-zinc-900"
      onContextMenu={preventContextMenu}
      onKeyDown={preventKeyboardSave}
    >
      {/* Profile header — brand logo in story ring */}
      <header className="border-b border-zinc-900/80">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-12">
          <div className="flex flex-col sm:flex-row sm:items-center gap-10 sm:gap-14">
            <div
              className="shrink-0 w-36 h-36 sm:w-44 sm:h-44 rounded-full p-1 mx-auto sm:mx-0"
              style={{
                background: 'conic-gradient(from 180deg, #f43f5e, #fb923c, #fbbf24, #f43f5e)',
              }}
            >
              <div className="w-full h-full rounded-full bg-black p-1">
                <div className="w-full h-full rounded-full bg-white border border-zinc-200 overflow-hidden flex items-center justify-center p-2 sm:p-2.5">
                  <img
                    src={GALLERY_BRAND_LOGO}
                    alt="Jacky Circuit"
                    className="w-full h-full object-contain select-none"
                    draggable={false}
                    onContextMenu={preventContextMenu}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0 text-center sm:text-left">
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white lowercase mb-5">
                jackycircuit
              </h1>

              <div className="flex gap-8 sm:gap-10 justify-center sm:justify-start mb-6 text-sm">
                <div>
                  <span className="font-semibold text-white">{mediaItems.length}</span>
                  <span className="text-zinc-500 ml-2">posts</span>
                </div>
                <div>
                  <span className="font-semibold text-white">{eventCount ?? '—'}</span>
                  <span className="text-zinc-500 ml-2">events</span>
                </div>
                <div>
                  <span className="font-semibold text-white">{mediaItems.filter((m) => m.is_featured).length}</span>
                  <span className="text-zinc-500 ml-2">spotlights</span>
                </div>
              </div>

              <div className="max-w-xl mx-auto sm:mx-0">
                <p className="font-semibold text-[15px] mb-1">Jacky Circuit · Motorsport park</p>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Professional drag strip and drift track. Latest photos and reels from the paddock.
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex justify-center gap-8 sm:gap-16 border-t border-zinc-900 max-w-5xl mx-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setActiveTab(t.id);
              setLightboxIndex(null);
            }}
            className={`py-4 text-[11px] font-semibold uppercase tracking-[0.14em] border-t -mt-px transition-colors ${
              activeTab === t.id ? 'border-white text-white' : 'border-transparent text-zinc-600 hover:text-zinc-400'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="max-w-5xl mx-auto px-1 sm:px-4 pb-12 pt-2">
        {filteredItems.length === 0 ? (
          <p className="text-center text-zinc-500 py-16 text-lg">No media in this view yet.</p>
        ) : (
          <div className="grid grid-cols-3 gap-0.5 sm:gap-1">
            {filteredItems.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => openAt(index)}
                className="group relative aspect-square bg-zinc-950 overflow-hidden border-0 p-0 cursor-pointer"
              >
                {item.media_type === 'image' ? (
                  <img
                    src={item.url}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover select-none"
                    draggable={false}
                    onContextMenu={preventContextMenu}
                  />
                ) : (
                  <video src={item.url} className="w-full h-full object-cover" preload="metadata" muted loop playsInline />
                )}
                {item.media_type === 'video' && (
                  <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center border border-white/15">
                    <span className="w-0 h-0 border-l-[7px] border-l-white border-y-[5px] border-y-transparent ml-0.5" aria-hidden />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center px-2">
                  <span className="text-white text-xs sm:text-sm font-semibold text-center line-clamp-2">{item.title}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxItem && lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/92 backdrop-blur-md flex flex-col"
          onClick={closeLightbox}
          role="presentation"
        >
          <div
            className="shrink-0 h-14 flex items-center justify-between px-4 border-b border-white/[0.08] bg-black/60 backdrop-blur-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeLightbox}
              className="flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium py-2"
            >
              <X className="w-5 h-5" />
              <span>Back to grid</span>
            </button>
            <div className="text-center flex-1 pointer-events-none px-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">jackycircuit · post</div>
              <div className="text-sm font-semibold truncate mt-0.5">{lightboxItem.title}</div>
            </div>
            <div className="w-20" aria-hidden />
          </div>

          <div className="flex-1 overflow-y-auto py-6 px-4 sm:px-6" onClick={(e) => e.stopPropagation()}>
            <article className="max-w-lg mx-auto bg-zinc-950/80 border border-zinc-800/80 rounded-xl overflow-hidden">
              <header className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800/80">
                <div className="w-9 h-9 rounded-full shrink-0 border border-zinc-600 bg-white overflow-hidden flex items-center justify-center p-0.5">
                  <img
                    src={GALLERY_BRAND_LOGO}
                    alt=""
                    className="w-full h-full object-contain"
                    draggable={false}
                    onContextMenu={preventContextMenu}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">jackycircuit</div>
                  <div className="text-[11px] text-zinc-500 truncate">{lightboxItem.media_type === 'video' ? 'Reel' : 'Photo'}</div>
                </div>
              </header>

              <div className="relative bg-black">
                {lightboxItem.media_type === 'image' ? (
                  <img
                    src={lightboxItem.url}
                    alt={lightboxItem.title}
                    loading="eager"
                    className="w-full max-h-[min(70vh,720px)] object-contain select-none"
                    draggable={false}
                    onContextMenu={preventContextMenu}
                  />
                ) : (
                  <video
                    src={lightboxItem.url}
                    className="w-full max-h-[min(70vh,720px)] object-contain"
                    controls
                    controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                    disablePictureInPicture
                    preload="auto"
                    autoPlay
                    onContextMenu={preventContextMenu}
                  />
                )}
              </div>

              <div className="px-4 py-4 text-sm">
                <p className="text-white leading-relaxed">
                  <span className="font-semibold mr-2">jackycircuit</span>
                  <span className="text-zinc-300">{lightboxItem.description || ' '}</span>
                </p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-600 mt-3">
                  {new Date(lightboxItem.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </article>

            <div className="max-w-lg mx-auto flex justify-between items-center mt-6 gap-4">
              <button
                type="button"
                onClick={goPrev}
                disabled={lightboxIndex <= 0}
                className="inline-flex items-center gap-1 rounded-full border border-white/10 px-4 py-2 text-sm text-white/80 hover:bg-white/5 disabled:opacity-30 disabled:pointer-events-none"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={lightboxIndex >= filteredItems.length - 1}
                className="inline-flex items-center gap-1 rounded-full border border-white/10 px-4 py-2 text-sm text-white/80 hover:bg-white/5 disabled:opacity-30 disabled:pointer-events-none"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
